import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Phone, KeyRound } from "lucide-react";

export default function OtpLogin({ onCancel }) {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [normalized, setNormalized] = useState("");
  const [code, setCode] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState("demo");

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone, name });
      setNormalized(data.phone);
      setProvider(data.provider || "demo");
      if (data.demo_mode && data.code) {
        setDemoCode(data.code);
        toast.success(`Demo OTP: ${data.code}`, { duration: 8000 });
      } else {
        toast.success(`OTP sent to ${data.phone} via ${(data.provider || "SMS").toUpperCase()}`);
      }
      setStep("code");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone: normalized, code, name });
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}!`);
      nav("/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (step === "phone") return (
    <form onSubmit={requestOtp} data-testid="otp-phone-form" className="space-y-4">
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-slate-500">Mobile number</span>
        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-slate-200 dark:border-white/10 bg-transparent px-4">
          <Phone className="w-4 h-4 text-slate-400"/>
          <input data-testid="otp-phone-input" required value={phone} onChange={(e)=>setPhone(e.target.value)}
            placeholder="+91 98765 43210 or 10-digit number"
            className="w-full py-3 bg-transparent focus:outline-none text-sm"/>
        </div>
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-slate-500">Your name (optional)</span>
        <input data-testid="otp-name-input" value={name} onChange={(e)=>setName(e.target.value)} placeholder="First-time users only"
          className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-white/10 bg-transparent px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"/>
      </label>
      <button data-testid="otp-send-btn" disabled={loading} className="w-full py-3.5 rounded-full btn-gold text-sm disabled:opacity-60">
        {loading ? "Sending..." : "Send OTP"}
      </button>
      <button type="button" onClick={onCancel} className="w-full text-xs text-slate-500 hover:text-[#D4AF37]">← Back to email sign-in</button>
    </form>
  );

  return (
    <form onSubmit={verify} data-testid="otp-code-form" className="space-y-4">
      <div className="text-sm text-slate-600 dark:text-slate-300">Enter the 6-digit code sent to <strong>{normalized}</strong></div>
      {demoCode && (
        <div className="rounded-xl p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-xs">
          <div className="uppercase tracking-widest text-[#D4AF37] font-bold mb-1">Demo Mode</div>
          <div className="text-slate-700 dark:text-slate-200">OTP: <span className="font-mono font-bold text-lg">{demoCode}</span></div>
          <div className="mt-1 text-[10px] text-slate-500">Add TWILIO_* or MSG91_* keys to .env to switch to real SMS.</div>
        </div>
      )}
      {!demoCode && provider !== "demo" && (
        <div className="rounded-xl p-2 bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-700 dark:text-emerald-300">Delivered via {provider.toUpperCase()}</div>
      )}
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-slate-500">6-digit OTP</span>
        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-slate-200 dark:border-white/10 bg-transparent px-4">
          <KeyRound className="w-4 h-4 text-slate-400"/>
          <input data-testid="otp-code-input" required value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
            maxLength={6} inputMode="numeric" placeholder="123456"
            className="w-full py-3 bg-transparent focus:outline-none text-lg font-mono tracking-widest"/>
        </div>
      </label>
      <button data-testid="otp-verify-btn" disabled={loading || code.length !== 6} className="w-full py-3.5 rounded-full btn-gold text-sm disabled:opacity-60">
        {loading ? "Verifying..." : "Verify & Sign In"}
      </button>
      <button type="button" onClick={() => { setStep("phone"); setCode(""); setDemoCode(""); }} className="w-full text-xs text-slate-500 hover:text-[#D4AF37]">← Change number</button>
    </form>
  );
}
